/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback } from '@web-stories-wp/react';
import { formatMsToHMS, getVideoLengthDisplay } from '@web-stories-wp/media';

/**
 * Internal dependencies
 */
import { useConfig, useLocalMedia, useStory } from '../../app';
import VideoTrimContext from './videoTrimContext';
import useVideoTrimMode from './useVideoTrimMode';
import useVideoNode from './useVideoNode';

function VideoTrimProvider({ children }) {
  const animatedThumbnail = useConfig(
    ({ animatedThumbnail }) => animatedThumbnail
  );
  const { selectedElements } = useStory(({ state: { selectedElements } }) => ({
    selectedElements,
  }));
  const { trimExistingVideo } = useLocalMedia((state) => ({
    trimExistingVideo: state.actions.trimExistingVideo,
  }));
  const { isTrimMode, hasTrimMode, isAnimatedThumbnail, toggleTrimMode } =
    useVideoTrimMode();
  const {
    hasChanged,
    currentTime,
    isLooping,
    startOffset,
    endOffset,
    maxOffset,
    setStartOffset,
    setEndOffset,
    setIsLooping,
    setVideoNode,
    commitOffsets,
    resetOffsets,
  } = useVideoNode({
    fixedDurationMs: animatedThumbnail
      ? animatedThumbnail.fixedDurationSeconds * 1000
      : null,
    initialStartOffset: animatedThumbnail
      ? animatedThumbnail.initialStartSeconds * 1000
      : 0,
    onDurationChange: animatedThumbnail?.onDurationChange,
  });

  const performTrim = useCallback(() => {
    if (isAnimatedThumbnail) {
      commitOffsets();
      animatedThumbnail.onTrim(startOffset / 1000);
      toggleTrimMode();
      return;
    }

    const { resource } = selectedElements[0];
    if (!resource) {
      return;
    }
    const lengthInSeconds = Math.round(endOffset / 1000 - startOffset / 1000);
    trimExistingVideo({
      resource: {
        ...resource,
        length: lengthInSeconds,
        lengthFormatted: getVideoLengthDisplay(lengthInSeconds),
      },
      start: formatMsToHMS(startOffset),
      end: formatMsToHMS(endOffset),
    });
    toggleTrimMode();
  }, [
    endOffset,
    animatedThumbnail,
    commitOffsets,
    isAnimatedThumbnail,
    startOffset,
    trimExistingVideo,
    selectedElements,
    toggleTrimMode,
  ]);

  const cancelTrim = useCallback(() => {
    resetOffsets();
    toggleTrimMode();
  }, [resetOffsets, toggleTrimMode]);

  const value = {
    state: {
      hasChanged,
      isTrimMode,
      hasTrimMode,
      isAnimatedThumbnail,
      currentTime,
      isLooping,
      startOffset,
      endOffset,
      maxOffset,
    },
    actions: {
      performTrim,
      cancelTrim,
      toggleTrimMode,
      setVideoNode,
      setStartOffset,
      setEndOffset,
      setIsLooping,
      resetOffsets,
    },
  };

  return (
    <VideoTrimContext.Provider value={value}>
      {children}
    </VideoTrimContext.Provider>
  );
}

VideoTrimProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default VideoTrimProvider;
